/**
 * CriteriaLinkPickerModal Component
 *
 * Multi-select picker modal for linking criteria to a playbook.
 * Shows active criteria grouped by market condition category.
 * Pre-selects already-linked criteria (active only - archived linked items
 * won't appear since picker only shows active criteria).
 *
 * Archived linked criteria are preserved on Apply - they are merged back
 * into the selection to prevent silent data loss.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'lucide-react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Link,
  Listbox,
  ListboxItem,
  ListboxSection,
} from '@enfomentis/ui';
import { criteriaService } from '@/services/CriteriaService';
import { marketConditionCategoryService } from '@/services/MarketConditionCategoryService';
import type { Selection } from '@react-types/shared';
import type { PlaybookCriteriaItem } from '@/types/schema/PlaybookCriteriaItem';
import type { MarketConditionCategory } from '@/types/schema/MarketConditionCategory';

export interface CriteriaLinkPickerModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Currently selected criteria IDs (for pre-selection) */
  selectedCriteriaIds: string[];
  /** Handler for selection change - receives new array of selected IDs */
  onSelectionChange: (selectedIds: string[]) => void;
}

/**
 * Empty state component when no active criteria exist
 */
const EmptyState: React.FC = () => (
  <div className="flex-1 flex items-center justify-center py-12">
    <div className="text-center max-w-md">
      <Tag className="w-16 h-16 text-text-secondary mx-auto mb-4" />
      <h4 className="text-lg font-medium text-text-primary mb-2">No Criteria Available</h4>
      <p className="text-sm text-text-secondary">
        Create criteria in Reference Data to link them to your playbooks.
      </p>
    </div>
  </div>
);

export const CriteriaLinkPickerModal: React.FC<CriteriaLinkPickerModalProps> = ({
  isOpen,
  onClose,
  selectedCriteriaIds,
  onSelectionChange,
}) => {
  const navigate = useNavigate();

  // Local selection state - synced from props when modal opens
  const [localSelectedKeys, setLocalSelectedKeys] = useState<Set<string>>(new Set());

  // Store original selected IDs to preserve archived criteria on Apply
  const originalSelectedIdsRef = useRef<string[]>([]);

  // Data fetching state
  const [activeCriteria, setActiveCriteria] = useState<PlaybookCriteriaItem[]>([]);
  const [categories, setCategories] = useState<MarketConditionCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Sync selection from props when modal opens
  // Filter to only IDs present in active criteria list
  useEffect(() => {
    if (isOpen) {
      // Store original selected IDs before filtering (to preserve archived on Apply)
      originalSelectedIdsRef.current = [...selectedCriteriaIds];

      // Load data when modal opens
      const loadData = async () => {
        setIsLoading(true);
        try {
          const [criteriaData, categoryData] = await Promise.all([
            criteriaService.getActiveCriteria(),
            marketConditionCategoryService.getAll(),
          ]);
          setActiveCriteria(criteriaData);
          setCategories(categoryData);

          // Pre-select only IDs that exist in the active criteria list
          const activeIds = new Set(criteriaData.map((c) => c.id));
          const validSelection = selectedCriteriaIds.filter((id) => activeIds.has(id));
          setLocalSelectedKeys(new Set(validSelection));
        } catch (error) {
          console.error('Failed to load criteria data:', error);
        } finally {
          setIsLoading(false);
        }
      };

      loadData();
    }
  }, [isOpen, selectedCriteriaIds]);

  // Build category lookup map
  const categoryMap = useMemo(() => {
    const map = new Map<string, MarketConditionCategory>();
    categories.forEach((cat) => map.set(cat.id, cat));
    return map;
  }, [categories]);

  // Group criteria by category, sort categories alphabetically, uncategorized at bottom
  const { categorizedGroups, uncategorized } = useMemo(() => {
    const grouped = new Map<string, PlaybookCriteriaItem[]>();
    const uncategorizedItems: PlaybookCriteriaItem[] = [];

    activeCriteria.forEach((item) => {
      if (item.marketConditionCategoryId) {
        const existing = grouped.get(item.marketConditionCategoryId) || [];
        grouped.set(item.marketConditionCategoryId, [...existing, item]);
      } else {
        uncategorizedItems.push(item);
      }
    });

    // Sort each category's items alphabetically by name
    grouped.forEach((items, categoryId) => {
      grouped.set(
        categoryId,
        [...items].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        )
      );
    });

    // Sort uncategorized items alphabetically by name
    const sortedUncategorized = [...uncategorizedItems].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );

    // Sort categories alphabetically by name
    const sortedCategories = Array.from(grouped.entries())
      .map(([categoryId, items]) => {
        const category = categoryMap.get(categoryId);
        return category ? { category, items } : null;
      })
      .filter((g): g is { category: MarketConditionCategory; items: PlaybookCriteriaItem[] } => g !== null)
      .sort((a, b) =>
        a.category.name.localeCompare(b.category.name, undefined, { sensitivity: 'base' })
      );

    return {
      categorizedGroups: sortedCategories,
      uncategorized: sortedUncategorized,
    };
  }, [activeCriteria, categoryMap]);

  /**
   * Handle selection change from Listbox
   * Guards against HeroUI Listbox emitting "all" for select-all scenarios
   */
  const handleSelectionChange = (keys: Selection): void => {
    // Guard against "all" sentinel before spreading
    if (keys === 'all') return;
    // Filter out any remaining non-string keys for safety
    const newKeys = new Set([...keys].filter((k): k is string => typeof k === 'string'));
    setLocalSelectedKeys(newKeys);
  };

  /**
   * Handle apply selection
   * Preserves archived linked criteria by merging them back in
   */
  const handleApply = (): void => {
    const selectedActiveIds = Array.from(localSelectedKeys);

    // Only merge archived IDs if we successfully loaded criteria data
    // (prevents treating all IDs as archived on fetch failure)
    if (activeCriteria.length > 0) {
      const activeIds = new Set(activeCriteria.map((c) => c.id));
      const archivedIds = originalSelectedIdsRef.current.filter((id) => !activeIds.has(id));

      // Merge with de-dupe using Set
      const merged = Array.from(new Set([...selectedActiveIds, ...archivedIds]));
      onSelectionChange(merged);
    } else {
      // On fetch failure, preserve original selection unchanged (with de-dupe for consistency)
      onSelectionChange(Array.from(new Set(originalSelectedIdsRef.current)));
    }

    onClose();
  };

  /**
   * Handle cancel - just close, selection resets on next open
   */
  const handleCancel = (): void => {
    onClose();
  };

  const hasItems = activeCriteria.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="lg"
      scrollBehavior="inside"
      placement="top-center"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex gap-2 items-center">
              <Tag className="w-5 h-5 text-primary" />
              <span>Select Criteria</span>
            </ModalHeader>

            <ModalBody className="gap-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-12 text-text-tertiary text-sm">
                  Loading...
                </div>
              ) : !hasItems ? (
                <EmptyState />
              ) : (
                <Listbox
                  aria-label="Criteria items"
                  selectionMode="multiple"
                  selectedKeys={localSelectedKeys}
                  onSelectionChange={handleSelectionChange}
                  variant="light"
                  classNames={{
                    base: 'max-h-[400px] overflow-auto',
                  }}
                >
                  {[
                    // Categorized items (sorted alphabetically by category name)
                    ...categorizedGroups.map(({ category, items }) => (
                      <ListboxSection
                        key={category.id}
                        title={category.name}
                        classNames={{
                          heading:
                            'text-sm font-semibold text-text-secondary flex w-full border-b border-default-200 pb-1',
                          base: 'mb-2',
                        }}
                      >
                        {items.map((item) => (
                          <ListboxItem
                            key={item.id}
                            textValue={item.name}
                            classNames={{
                              base: 'py-2 rounded-md my-1',
                              selectedIcon: 'text-primary scale-150 order-first',
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="w-2 h-2 rounded-full shrink-0"
                                style={{ backgroundColor: category.color }}
                              />
                              <span className="font-medium text-text-primary">{item.name}</span>
                            </div>
                          </ListboxItem>
                        ))}
                      </ListboxSection>
                    )),

                    // Uncategorized items at bottom
                    ...(uncategorized.length > 0
                      ? [
                          <ListboxSection
                            key="uncategorized"
                            title="Uncategorized"
                            classNames={{
                              heading:
                                'text-sm font-semibold text-text-tertiary flex w-full border-b border-default-200 pb-1',
                              base: 'mb-2',
                            }}
                          >
                            {uncategorized.map((item) => (
                              <ListboxItem
                                key={item.id}
                                textValue={item.name}
                                classNames={{
                                  base: 'py-2 rounded-md my-1',
                                  selectedIcon: 'text-primary scale-150 order-first',
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-text-primary">{item.name}</span>
                                </div>
                              </ListboxItem>
                            ))}
                          </ListboxSection>,
                        ]
                      : []),
                  ]}
                </Listbox>
              )}
            </ModalBody>

            <ModalFooter className="justify-between">
              {/* Manage Criteria shortcut */}
              <Link
                onPress={() => {
                  onClose();
                  navigate('/settings/strategies/reference-data?tab=criteria');
                }}
                showAnchorIcon
                size="sm"
                className="cursor-pointer"
              >
                Manage Criteria
              </Link>

              <div className="flex gap-2">
                <Button variant="light" onPress={handleCancel}>
                  Cancel
                </Button>
                <Button color="primary" onPress={handleApply}>
                  Apply
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
